<script setup lang="ts">
import { SharedData } from '@/@types'
import logo from '@/assets/logo-forum.svg'
import logoDark from '@/assets/logo.svg'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Link, router, usePage } from '@inertiajs/vue3'
import { ChevronDown, ShoppingCart } from 'lucide-vue-next'
import { computed } from 'vue'
import { Button } from '../ui/button'
import Desktop from './Desktop.vue'
import LanguageSwitch from './LanguageSwitch.vue'
import Mobile from './Mobile.vue'

const page = usePage<SharedData>()

const cart = computed(() => {
    return page.props.current_cart
})

withDefaults(
    defineProps<{
        dark?: boolean
        logoType?: boolean
    }>(),
    {
        dark: false,
    },
)

const languageSwitcher = import.meta.env.VITE_LANGUAGE_SWITCHER

const userInitials = computed(() => {
    const user = page.props.user
    if (!user) return ''
    if (user.name) return user.name.slice(0, 2)
    return 'U'
})

const displayName = computed(() => {
    const user = page.props.user
    if (!user) return ''
    return user.name ?? 'Usuário'
})
</script>

<template>
    <header class="group relative isolate z-40 bg-white data-[dark=false]:shadow-sm data-[data=false]:bg-transparent" :data-dark="dark">
        <div
            class="top-0 right-0 left-0 z-40 container flex min-h-[68px] items-center justify-between gap-4 p-4 group-data-[dark=false]:relative group-data-[dark=false]:text-gray-900 group-data-[dark=true]:absolute group-data-[dark=true]:text-white"
        >
            <Link href="/" aria-label="Home">
                <img :src="logo" alt="" class="h-full w-36" v-if="dark && !logoType" />
                <img :src="logoDark" alt="" class="h-full w-14" v-if="logoType || !dark" />
            </Link>

            <Desktop />

            <div class="flex items-center gap-2">
                <Link v-if="!page.props.user" href="/session_cart" class="flex items-center gap-2 text-sm/none font-medium">
                    <ShoppingCart />
                    <span v-if="cart"> ({{ cart.items.length }}) </span>
                </Link>
                <div v-if="page.props.user" class="flex items-center gap-3 md:gap-5">
                    <Link href="/cart" class="flex items-center gap-2 text-sm/none font-medium">
                        <ShoppingCart />
                        <span> ({{ cart.items.length }}) </span>
                    </Link>

                    <div>
                        <DropdownMenu>
                            <DropdownMenuTrigger class="font-inter flex cursor-pointer items-center gap-1 text-sm sm:gap-2">
                                <Avatar>
                                    <AvatarImage :src="page.props.user.avatar_url || ''" alt="Avatar" fetchpriority="high" />
                                    <AvatarFallback class="text-black uppercase">
                                        {{ userInitials }}
                                    </AvatarFallback>
                                </Avatar>
                                <span class="line-clamp-1 hidden md:block"> {{ displayName }} </span>
                                <ChevronDown />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent class="w-full max-w-xl">
                                <DropdownMenuItem class="cursor-pointer">
                                    <Link href="/users/edit" class="w-full cursor-pointer text-left">
                                        {{ $t('Edit Profile') }}
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem class="cursor-pointer">
                                    <button @click="router.delete('/users/sign_out', {})" class="w-full cursor-pointer text-left">Logout</button>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                <div v-else class="flex items-center gap-1">
                    <Button :as="Link" href="/users/sign_in" variant="ghost" size="sm" class="text-sm"> {{ $t('Login') }}</Button>
                    <Button :as="Link" href="/users/sign_up" size="sm" class="text-sm">{{ $t('Register') }}</Button>
                </div>
                <LanguageSwitch v-if="page.props.user?.role !== 'admin' && languageSwitcher && languageSwitcher === 'true'" />

                <Mobile />
            </div>
        </div>
    </header>
</template>
